import {
	Box,
	Button,
	CircularProgress,
	Paper,
	TextField,
	Typography,
	Autocomplete,
} from "@mui/material";
import { useEffect, useState, useRef } from "react";
import toast from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";
import SignatureCanvas from "react-signature-canvas";
import MDButton from "../../components/MDButton";
import DashboardLayout from "../../examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "../../examples/Navbars/DashboardNavbar";
import apiService from "../../services/api-service";

const NonConformitaSoluzione = () => {
	const { id } = useParams();
	const navigate = useNavigate();
	const sigCanvas = useRef(null);

	const [nc, setNc] = useState(null);
	const [users, setUsers] = useState([]);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);

	const [formData, setFormData] = useState({
		assegnato_a: null,
		tipo_soluzione: "",
		descrizione: "",
		data_chiusura_prevista: "",
	});

	const [fieldErrors, setFieldErrors] = useState({
		assegnato_a: false,
		tipo_soluzione: false,
		descrizione: false,
		data_chiusura_prevista: false,
		firma: false,
	});

	const tipiSoluzione = [
		{ value: "RILAVORARE", label: "Rilavorare" },
		{ value: "ACCETTARE", label: "Accettare" },
		{ value: "IMPIEGO_ALTERNATIVO", label: "Impiego Alternativo" },
		{ value: "RESTITUIRE", label: "Restituire al Fornitore" },
		{ value: "ELIMINARE", label: "Eliminare/Scartare" },
	];

	useEffect(() => {
		fetchData();
	}, [id]);

	const fetchData = async () => {
		setLoading(true);
		try {
			// Fetch NC details
			const ncResponse = await apiService.get(`/non-conformita/${id}/`, {}, {});

			if (ncResponse.stato !== "APERTA") {
				toast.error(`NC deve essere in stato APERTA (stato attuale: ${ncResponse.stato_display})`);
				navigate("/quality/non-conformita");
				return;
			}

			setNc(ncResponse);

			// Fetch users list
			const usersResponse = await apiService.get("/user-internal-management/", {}, {});
			if (usersResponse && Array.isArray(usersResponse)) {
				setUsers(usersResponse);
			}
		} catch (error) {
			toast.error("Errore nel caricamento dati");
			navigate("/quality/non-conformita");
		} finally {
			setLoading(false);
		}
	};

	const handleInputChange = (name, value) => {
		setFormData((prev) => ({ ...prev, [name]: value }));
		setFieldErrors((prev) => ({ ...prev, [name]: false }));
	};

	const clearSignature = () => {
		if (sigCanvas.current) {
			sigCanvas.current.clear();
			setFieldErrors((prev) => ({ ...prev, firma: false }));
		}
	};

	const handleSave = async () => {
		// Validazione
		const errors = {
			assegnato_a: !formData.assegnato_a,
			tipo_soluzione: !formData.tipo_soluzione,
			descrizione: !formData.descrizione || formData.descrizione.trim() === "",
			data_chiusura_prevista: !formData.data_chiusura_prevista,
			firma: !sigCanvas.current || sigCanvas.current.isEmpty(),
		};

		setFieldErrors(errors);

		if (Object.values(errors).some((err) => err)) {
			toast.error("Compilare tutti i campi obbligatori");
			return;
		}

		setSaving(true);

		try {
			const firmaBase64 = sigCanvas.current.toDataURL();

			const payload = {
				assegnato_a: formData.assegnato_a,
				tipo_soluzione: formData.tipo_soluzione,
				descrizione: formData.descrizione,
				data_chiusura_prevista: formData.data_chiusura_prevista,
				firma_soluzione: firmaBase64,
			};

			await apiService.post(`/non-conformita/${id}/proponi-soluzione/`, {}, payload);
			toast.success("Soluzione proposta con successo");

			setTimeout(() => {
				navigate("/quality/non-conformita");
			}, 1500);
		} catch (error) {
			console.error("Errore salvataggio soluzione:", error);
			toast.error(error.response?.data?.error || "Errore durante il salvataggio");
		} finally {
			setSaving(false);
		}
	};

	if (loading) {
		return (
			<DashboardLayout>
				<DashboardNavbar />
				<Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
					<CircularProgress />
				</Box>
			</DashboardLayout>
		);
	}

	return (
		<DashboardLayout>
			<DashboardNavbar />
			<Box p={3}>
				<Paper elevation={3} sx={{ p: 4, maxWidth: 900, margin: "0 auto" }}>
					<Typography variant="h4" gutterBottom color="primary">
						Proponi Soluzione - NC {nc?.numero}
					</Typography>

					<Box mt={2} mb={3}>
						<Typography variant="body2" color="textSecondary">
							Cantiere: <strong>{nc?.cantiere_nome}</strong>
						</Typography>
						<Typography variant="body2" color="textSecondary">
							Descrizione NC: <strong>{nc?.descrizione}</strong>
						</Typography>
					</Box>

					<Box mt={3} display="flex" flexDirection="column" gap={3}>
						{/* Assegnato a */}
						<Autocomplete
							options={users}
							getOptionLabel={(option) => option.first_name + " " + option.last_name || ""}
							value={users.find((u) => u.id === formData.assegnato_a) || null}
							onChange={(event, newValue) => {
								handleInputChange("assegnato_a", newValue ? newValue.id : null);
							}}
							renderInput={(params) => (
								<TextField
									{...params}
									label="Assegnato a *"
									error={fieldErrors.assegnato_a}
									helperText={
										fieldErrors.assegnato_a
											? "Campo obbligatorio"
											: "Responsabile implementazione soluzione"
									}
								/>
							)}
							fullWidth
						/>

						{/* Tipo Soluzione */}
						<Autocomplete
							options={tipiSoluzione}
							getOptionLabel={(option) => option.label || ""}
							value={tipiSoluzione.find((t) => t.value === formData.tipo_soluzione) || null}
							onChange={(event, newValue) => {
								handleInputChange("tipo_soluzione", newValue ? newValue.value : "");
							}}
							renderInput={(params) => (
								<TextField
									{...params}
									label="Tipo Soluzione *"
									error={fieldErrors.tipo_soluzione}
									helperText={fieldErrors.tipo_soluzione ? "Campo obbligatorio" : ""}
								/>
							)}
							fullWidth
						/>

						{/* Descrizione */}
						<TextField
							label="Descrizione Soluzione *"
							multiline
							rows={6}
							value={formData.descrizione}
							onChange={(e) => handleInputChange("descrizione", e.target.value)}
							error={fieldErrors.descrizione}
							helperText={
								fieldErrors.descrizione
									? "Campo obbligatorio"
									: "Dettagli operativi della soluzione proposta"
							}
							fullWidth
						/>

						{/* Data Chiusura Prevista */}
						<TextField
							label="Data Chiusura Prevista *"
							type="date"
							value={formData.data_chiusura_prevista}
							onChange={(e) => handleInputChange("data_chiusura_prevista", e.target.value)}
							error={fieldErrors.data_chiusura_prevista}
							helperText={
								fieldErrors.data_chiusura_prevista
									? "Campo obbligatorio"
									: "Data entro cui si prevede di completare la soluzione"
							}
							InputLabelProps={{ shrink: true }}
							fullWidth
						/>

						{/* Firma Digitale */}
						<Box>
							<Typography variant="subtitle1" gutterBottom>
								Firma Digitale *
							</Typography>
							<Box
								border={fieldErrors.firma ? "2px solid red" : "1px solid #ccc"}
								borderRadius={2}
								p={1}
								bgcolor="#f9f9f9"
							>
								<SignatureCanvas
									ref={sigCanvas}
									canvasProps={{
										width: 800,
										height: 200,
										className: "signature-canvas",
										style: { border: "1px dashed #999" },
									}}
								/>
							</Box>
							<Box mt={1} display="flex" gap={2}>
								<Button variant="outlined" size="small" onClick={clearSignature}>
									Cancella Firma
								</Button>
								{fieldErrors.firma && (
									<Typography variant="caption" color="error">
										Firma obbligatoria
									</Typography>
								)}
							</Box>
						</Box>

						{/* Actions */}
						<Box display="flex" gap={2} justifyContent="flex-end" mt={2}>
							<Button
								variant="outlined"
								onClick={() => navigate("/quality/non-conformita")}
								disabled={saving}
							>
								Annulla
							</Button>
							<MDButton
								variant="gradient"
								color="warning"
								onClick={handleSave}
								disabled={saving}
							>
								{saving ? <CircularProgress size={24} /> : "Proponi Soluzione"}
							</MDButton>
						</Box>
					</Box>
				</Paper>
			</Box>
		</DashboardLayout>
	);
};

export default NonConformitaSoluzione;
